import React, { useState } from 'react';
import axios from 'axios';
import { biosdkOperationUrl, CONVERTER_SINGLE_API_BASE, logBiosdkUiRoute } from './config';
import { Eye, Upload, Loader2, AlertCircle, CheckCircle2, XCircle, Clock, ArrowLeftRight } from 'lucide-react';
import { Button } from './components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './components/ui/card';
import { Label } from './components/ui/label';
import { Select } from './components/ui/select';
import { Badge } from './components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from './components/ui/alert';
import { resolveIsMatch, parseOptionalNumber } from './matchResultUtils';

function pickIrisDecision(decisions) {
  if (!decisions || typeof decisions !== 'object') return {};
  if (decisions.IRIS) return decisions.IRIS;
  const keys = Object.keys(decisions);
  const k = keys.find((x) => /^iris$/i.test(x));
  return k ? decisions[k] : keys[0] ? decisions[keys[0]] : {};
}

function normalizeAnalyticsInfo(raw) {
  if (raw == null) return {};
  if (Array.isArray(raw)) {
    const out = {};
    for (const item of raw) {
      if (item && typeof item === 'object') {
        const key = item.key ?? item.name ?? item.k;
        const val = item.value ?? item.val ?? item.v;
        if (key != null) out[String(key)] = val;
      }
    }
    return out;
  }
  if (typeof raw === 'object') return raw;
  return {};
}

function getAnalyticsValue(analyticsObj, ...names) {
  const lower = names.map((n) => n.toLowerCase());
  for (const key of Object.keys(analyticsObj)) {
    if (lower.includes(key.toLowerCase())) {
      return analyticsObj[key];
    }
  }
  return undefined;
}

function extractIrisDecisionErrors(decision) {
  const raw = decision?.errors ?? decision?.Errors;
  if (raw == null) return [];
  if (Array.isArray(raw)) {
    return raw
      .map((e) => (typeof e === 'string' ? e : e?.message ?? String(e)))
      .filter(Boolean);
  }
  return [String(raw)];
}

function extractIrisScoreThreshold(decision) {
  const analyticsInfo = normalizeAnalyticsInfo(
    decision.analyticsInfo ?? decision.analytics ?? decision.analyticsinfo
  );
  const score = parseOptionalNumber(
    getAnalyticsValue(analyticsInfo, 'score', 'matchScore', 'matchscore') ?? decision.score
  );
  const threshold = parseOptionalNumber(
    getAnalyticsValue(analyticsInfo, 'threshold', 'matchThreshold', 'matchthreshold') ?? decision.threshold
  );
  return { score, threshold, analyticsInfo };
}

function detectOpenIrisTemplateBuffer(arrayBuffer) {
  try {
    const text = new TextDecoder().decode(new Uint8Array(arrayBuffer));
    const parsed = JSON.parse(text);
    return !!(parsed && typeof parsed === 'object' && parsed.iris_code && parsed.mask_code && parsed.format);
  } catch {
    return false;
  }
}

function extractApiErrorFromArrayBuffer(arrayBuffer) {
  try {
    const text = new TextDecoder().decode(new Uint8Array(arrayBuffer));
    if (!text || !text.trim().startsWith('{')) return null;
    const parsed = JSON.parse(text);
    const msg =
      parsed?.errors?.[0]?.message ||
      parsed?.response?.statusMessage ||
      parsed?.message ||
      null;
    return msg ? String(msg) : null;
  } catch {
    return null;
  }
}

function ensureBinaryPayloadLooksValid(arrayBuffer, contextLabel) {
  if (!arrayBuffer || arrayBuffer.byteLength === 0) {
    throw new Error(`${contextLabel} returned empty data.`);
  }
  const apiErr = extractApiErrorFromArrayBuffer(arrayBuffer);
  if (apiErr) {
    throw new Error(`${contextLabel} failed: ${apiErr}`);
  }
}

function IrisMatcher() {
  const [sampleFile, setSampleFile] = useState(null);
  const [galleryFile, setGalleryFile] = useState(null);
  const [sampleIrisType, setSampleIrisType] = useState('Left');
  const [galleryIrisType, setGalleryIrisType] = useState('Left');
  const [samplePreview, setSamplePreview] = useState(null);
  const [galleryPreview, setGalleryPreview] = useState(null);
  const [jp2isoSampleUrl, setJp2IsoSampleUrl] = useState(null);
  const [jp2isoGalleryUrl, setJp2IsoGalleryUrl] = useState(null);
  const [timing, setTiming] = useState({ total: 0, sampleConvert: 0, galleryConvert: 0, matchTime: 0 });
  const [error, setError] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [matchResult, setMatchResult] = useState(null);
  const [galleryExtracted, setGalleryExtracted] = useState(false);

  const toSignedByteJson = (arrayBuffer) => {
    const unsigned = new Uint8Array(arrayBuffer);
    const signed = Array.from(unsigned, (v) => (v > 127 ? v - 256 : v));
    return JSON.stringify(signed);
  };

  const handleFileChange = async (e, type) => {
    const selectedFile = e.target.files[0];
    const setFile = type === 'sample' ? setSampleFile : setGalleryFile;
    const setPreview = type === 'sample' ? setSamplePreview : setGalleryPreview;
    const setJp2 = type === 'sample' ? setJp2IsoSampleUrl : setJp2IsoGalleryUrl;

    setFile(selectedFile);
    setMatchResult(null);
    setError(null);
    setJp2(null);

    if (!selectedFile) {
      return;
    }

    const fileExtension = selectedFile.name.split('.').pop().toLowerCase();

    try {
      if (type === 'gallery') {
        if (galleryExtracted && fileExtension !== 'iso') {
          setError('Gallery image is marked as extracted. Please upload an ISO file (.iso).');
          setFile(null);
          setPreview(null);
          return;
        }
      }
      if (['jpeg', 'jpg', 'bmp', 'png'].includes(fileExtension)) {
        setPreview(URL.createObjectURL(selectedFile));
      } else if (fileExtension === 'iso') {
        try {
          const isoFormData = new FormData();
          isoFormData.append('file', selectedFile);
          const response = await axios.post(`${CONVERTER_SINGLE_API_BASE}/convertFromIsoFile?modality=IRIS`, isoFormData, {
            headers: { 'Content-Type': 'multipart/form-data' },
            responseType: 'arraybuffer',
          });
          const jpegBlob = new Blob([response.data], { type: 'image/jpeg' });
          setPreview(URL.createObjectURL(jpegBlob));
        } catch (_) {
          // Some ISO inputs may represent extracted templates and are not previewable as image bytes.
          // Do not block matching for such files; just skip preview.
          setPreview(null);
        }
      } else if (fileExtension === 'jp2') {
        setError('JP2 is not supported in direct mode. Use jpg/jpeg/png/bmp/iso.');
        setFile(null);
        setPreview(null);
        return;
      } else {
        setError('Unsupported file type.');
        setFile(null);
      }
    } catch (err) {
      setError(err?.message || 'An error occurred while processing the file.');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsProcessing(true);
    setError(null);
    setMatchResult(null);

    if (!sampleFile || !galleryFile) {
      setError('Please upload both sample and gallery iris images.');
      setIsProcessing(false);
      return;
    }

    let sampleBdb = '';
    let galleryBdb = '';
    let sampleBdbLevel = 'RAW';
    let galleryBdbLevel = 'RAW';
    let sampleConvertMs = 0;
    let galleryConvertMs = 0;

    try {
      const t0 = performance.now();

      const loadSampleIso = async () => {
        const ext = sampleFile.name.split('.').pop().toLowerCase();
        const tStart = performance.now();
        if (['jpeg', 'jpg', 'bmp', 'png'].includes(ext)) {
          const res = await sampleFile.arrayBuffer();
          sampleConvertMs = performance.now() - tStart;
          return { bdb: toSignedByteJson(res), level: 'RAW' };
        }
        if (ext === 'jp2') {
          throw new Error('JP2 is not supported for direct mode. Use jpg/png/bmp/iso.');
        }
        if (ext === 'iso') {
          const buf = await sampleFile.arrayBuffer();
          sampleConvertMs = performance.now() - tStart;
          if (!buf || buf.byteLength === 0) {
            throw new Error('Sample ISO file is empty.');
          }
          const isoApiErr = extractApiErrorFromArrayBuffer(buf);
          if (isoApiErr) {
            throw new Error(`Sample ISO file is not valid binary ISO data: ${isoApiErr}`);
          }
          if (detectOpenIrisTemplateBuffer(buf)) {
            return { bdb: toSignedByteJson(buf), level: 'INTERMEDIATE' };
          }
          return { bdb: toSignedByteJson(buf), level: 'RAW' };
        }
        throw new Error('Unsupported sample file type.');
      };

      const loadGalleryIso = async () => {
        const ext = galleryFile.name.split('.').pop().toLowerCase();
        const tStart = performance.now();
        if (ext === 'iso') {
          const buf = await galleryFile.arrayBuffer();
          galleryConvertMs = performance.now() - tStart;
          if (!buf || buf.byteLength === 0) {
            throw new Error('Gallery ISO file is empty.');
          }
          const isoApiErr = extractApiErrorFromArrayBuffer(buf);
          if (isoApiErr) {
            throw new Error(`Gallery ISO file is not valid binary ISO data: ${isoApiErr}`);
          }
          if (detectOpenIrisTemplateBuffer(buf)) {
            return { bdb: toSignedByteJson(buf), level: 'INTERMEDIATE' };
          }
          return { bdb: toSignedByteJson(buf), level: 'RAW' };
        }
        if (!galleryExtracted && ['jpeg', 'jpg', 'bmp', 'png'].includes(ext)) {
          const res = await galleryFile.arrayBuffer();
          galleryConvertMs = performance.now() - tStart;
          return { bdb: toSignedByteJson(res), level: 'RAW' };
        }
        if (!galleryExtracted && ext === 'jp2') {
          throw new Error('JP2 is not supported for direct mode. Use jpg/png/bmp/iso.');
        }
        if (galleryExtracted) {
          throw new Error('Gallery image is marked as extracted. Upload only ISO files (.iso).');
        }
        throw new Error('Gallery image is not marked as extracted. Upload image files only (jpg/png/bmp/iso).');
      };

      const sampleLoad = await loadSampleIso();
      sampleBdb = sampleLoad.bdb;
      sampleBdbLevel = sampleLoad.level;
      const galleryLoad = await loadGalleryIso();
      galleryBdb = galleryLoad.bdb;
      galleryBdbLevel = galleryLoad.level;

      const templateResponse = await fetch('/iris_match_inner_request-template.json');
      if (!templateResponse.ok) throw new Error(`Template fetch failed with status: ${templateResponse.status}`);

      let templateText = await templateResponse.text();
      templateText = templateText
        .replace(/<sample_iris_subtype>/g, sampleIrisType)
        .replace(/<gallery_iris_subtype>/g, galleryIrisType)
        .replace(/<sample_bdb_level>/g, sampleBdbLevel)
        .replace(/<gallery_bdb_level>/g, galleryBdbLevel)
        .replace(/sample_bdb/g, sampleBdb)
        .replace(/gallery_bdb/g, galleryBdb);

      const versionRequestTemplateResponse = await fetch('/match_request-template.json');
      if (!versionRequestTemplateResponse.ok) {
        throw new Error(`Version request template fetch failed with status: ${versionRequestTemplateResponse.status}`);
      }

      const versionRequestTemplateText = await versionRequestTemplateResponse.text();
      const base64EncodedTemplate = btoa(templateText);
      const finalRequestBody = JSON.parse(versionRequestTemplateText.replace('encoded_request', base64EncodedTemplate));
      finalRequestBody.operation = 'match';

      const matchStart = performance.now();
      const isRawInput =
        sampleFile.name.split('.').pop().toLowerCase() !== 'iso' ||
        galleryFile.name.split('.').pop().toLowerCase() !== 'iso';
      const matchUrl = biosdkOperationUrl('process', isRawInput);
      logBiosdkUiRoute('IrisMatcher', isRawInput ? 'rawRoute' : 'isoRoute', matchUrl, {
        operation: 'process',
      });
      const matchResponse = await axios.post(matchUrl, finalRequestBody, {
        headers: { 'Content-Type': 'application/json' },
      });
      const matchHttpMs = performance.now() - matchStart;

      if (matchResponse.data.errors && matchResponse.data.errors.length > 0) {
        setError(`Error: ${matchResponse.data.errors[0].message}`);
        setMatchResult(null);
        return;
      }

      const responseItem = matchResponse?.data?.response?.response?.[0] || {};
      const decisions = responseItem.decisions || {};
      const decision = pickIrisDecision(decisions);
      const decisionErrors = extractIrisDecisionErrors(decision);
      if (decisionErrors.length > 0) {
        setError(decisionErrors.join(' '));
        setMatchResult(null);
        setTiming({
          total: performance.now() - t0,
          sampleConvert: sampleConvertMs,
          galleryConvert: galleryConvertMs,
          matchTime: matchHttpMs,
        });
        return;
      }
      const matchEnum = decision.match ?? decision.Match;
      if (String(matchEnum || '').toUpperCase() === 'ERROR') {
        setError('Iris match returned an error. Check BioSDK logs for details.');
        setMatchResult(null);
        setTiming({
          total: performance.now() - t0,
          sampleConvert: sampleConvertMs,
          galleryConvert: galleryConvertMs,
          matchTime: matchHttpMs,
        });
        return;
      }
      const { score, threshold, analyticsInfo } = extractIrisScoreThreshold(decision);
      const comparisonMode = String(
        analyticsInfo.iris_comparison_mode ?? analyticsInfo.IRIS_COMPARISON_MODE ?? ''
      ).toLowerCase();
      const crossEye = comparisonMode === 'cross_eye';
      // Cross-eye: show score for reference; verification is still "no match" (different eyes).
      const isMatch =
        !crossEye && resolveIsMatch(score, threshold, decision.match ?? decision.Match);

      const confidencePct =
        score != null && !Number.isNaN(Number(score)) ? (Number(score) * 100).toFixed(1) : 'N/A';

      setMatchResult({
        status: isMatch ? 'MATCHED' : 'NOT_MATCHED',
        score,
        threshold,
        confidence: confidencePct,
        details: {
          sampleIrisType,
          galleryIrisType,
          algorithm: 'OpenIris (masked Hamming → similarity)',
          matchCondition: analyticsInfo.MatchConditon || analyticsInfo.matchCondition || 'score >= threshold',
          irisComparisonMode: comparisonMode || undefined,
          crossEyeComparison: crossEye,
          crossEyeNote: crossEye
            ? 'Sample and gallery are different eyes (e.g. left vs right). Similarity is shown for reference only; identity checks normally use the same eye on both sides.'
            : undefined,
        },
      });

      const matchDuration =
        parseOptionalNumber(
          analyticsInfo.matchDuration ?? analyticsInfo.match_duration ?? responseItem.matchDuration
        ) ?? matchHttpMs;

      setTiming({
        total: performance.now() - t0,
        sampleConvert: sampleConvertMs,
        galleryConvert: galleryConvertMs,
        matchTime: matchDuration,
      });
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'An error occurred during matching';
      setError(errorMessage);
      setMatchResult(null);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleReset = () => {
    setSampleFile(null);
    setGalleryFile(null);
    setSamplePreview(null);
    setGalleryPreview(null);
    setJp2IsoSampleUrl(null);
    setJp2IsoGalleryUrl(null);
    setSampleIrisType('Left');
    setGalleryIrisType('Left');
    setGalleryExtracted(false);
    setMatchResult(null);
    setError(null);
  };

  const scoreBarWidth = () => {
    if (matchResult == null || matchResult.score == null) return 0;
    const s = Number(matchResult.score);
    if (Number.isNaN(s)) return 0;
    return Math.min(100, Math.max(0, s * 100));
  };

  const thresholdMarkerLeft = () => {
    if (matchResult == null || matchResult.threshold == null) return 0;
    const t = Number(matchResult.threshold);
    if (Number.isNaN(t)) return 0;
    return Math.min(100, Math.max(0, t * 100));
  };

  const ImageUploadCard = ({ title, file, preview, irisType, setIrisType, onFileChange, type }) => (
    <Card className="flex-1">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg">{title}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <Label className="text-gray-700 font-medium">Iris Type</Label>
          <Select value={irisType} onChange={(e) => setIrisType(e.target.value)} className="w-full">
            <option value="Left">Left Eye</option>
            <option value="Right">Right Eye</option>
          </Select>
        </div>

        <div className="upload-zone-small">
          <input
            type="file"
            id={`file-${type}`}
            accept={type === 'gallery'
              ? (galleryExtracted ? '.iso' : '.png,.jpg,.jpeg,.bmp,.jp2,.iso')
              : '.png,.jpg,.jpeg,.bmp,.jp2,.iso'}
            onChange={(e) => onFileChange(e, type)}
            className="hidden"
          />
          <label htmlFor={`file-${type}`} className="cursor-pointer">
            {preview ? (
              <div className="relative">
                <img src={preview} alt={`${title} preview`} className="w-full h-32 object-contain rounded" />
                <Badge variant="iris" className="absolute top-1 right-1 text-xs">
                  {irisType}
                </Badge>
              </div>
            ) : (
              <>
                <Upload className="h-8 w-8 mx-auto text-gray-400 mb-2" />
                <p className="text-xs text-gray-600">Click to upload</p>
              </>
            )}
          </label>
        </div>
        {file && <p className="text-xs text-gray-500 text-center truncate">{file.name}</p>}
      </CardContent>
    </Card>
  );

  return (
    <div className="biochq-page">
      <div className="biochq-container-wide">
        <Card className="biochq-card iris">
          <CardHeader className="text-center">
            <div className="flex items-center justify-center gap-2 mb-2">
              <Eye className="h-8 w-8 text-iris" />
              <CardTitle className="text-3xl font-bold text-gray-800">Iris Matcher</CardTitle>
            </div>
            <CardDescription className="text-gray-600">
              Compare two iris images to verify identity using biometric matching .
            </CardDescription>
          </CardHeader>

          <CardContent>
            <div className="space-y-6">
              <div className="max-w-md mx-auto">
                <div className="flex items-center gap-2 justify-center">
                  <input
                    type="checkbox"
                    id="irisGalleryExtracted"
                    checked={galleryExtracted}
                    onChange={(e) => {
                      setGalleryExtracted(e.target.checked);
                      setGalleryFile(null);
                      setGalleryPreview(null);
                      setJp2IsoGalleryUrl(null);
                      setMatchResult(null);
                      setError(null);
                    }}
                    className="rounded"
                  />
                  <Label htmlFor="irisGalleryExtracted" className="text-sm text-gray-600">
                    Gallery image is already extracted.
                  </Label>
                </div>
              </div>
              <div className="flex gap-4 items-stretch">
                <ImageUploadCard
                  title="Sample Iris"
                  file={sampleFile}
                  preview={samplePreview}
                  irisType={sampleIrisType}
                  setIrisType={setSampleIrisType}
                  onFileChange={handleFileChange}
                  type="sample"
                />

                <div className="flex items-center justify-center">
                  <div className="compare-icon">
                    <ArrowLeftRight className="h-6 w-6 text-primary" />
                  </div>
                </div>

                <ImageUploadCard
                  title="Gallery Iris"
                  file={galleryFile}
                  preview={galleryPreview}
                  irisType={galleryIrisType}
                  setIrisType={setGalleryIrisType}
                  onFileChange={handleFileChange}
                  type="gallery"
                />
              </div>

              <div className="flex gap-3 justify-center">
                <Button
                  onClick={handleSubmit}
                  disabled={!sampleFile || !galleryFile || isProcessing}
                  variant="iris"
                  size="lg"
                >
                  {isProcessing ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Matching...
                    </>
                  ) : (
                    <>
                      <Eye className="mr-2 h-4 w-4" />
                      Match Irises
                    </>
                  )}
                </Button>
                <Button onClick={handleReset} variant="outline" size="lg" disabled={isProcessing}>
                  Reset
                </Button>
              </div>

              {error && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertTitle>Error</AlertTitle>
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              {matchResult && (
                <div className="space-y-4">
                  <Alert variant={matchResult.status === 'MATCHED' ? 'success' : 'warning'}>
                    {matchResult.status === 'MATCHED' ? (
                      <CheckCircle2 className="h-4 w-4" />
                    ) : (
                      <XCircle className="h-4 w-4" />
                    )}
                    <AlertTitle className="text-lg">
                      {matchResult.status === 'MATCHED' ? 'Match Found!' : 'No Match'}
                    </AlertTitle>
                    <AlertDescription className="space-y-2">
                      <p>
                        Similarity: {matchResult.confidence}%
                        {matchResult.threshold != null && !Number.isNaN(Number(matchResult.threshold))
                          ? ` (threshold: ${(Number(matchResult.threshold) * 100).toFixed(0)}%)`
                          : ''}
                      </p>
                      {matchResult.details?.crossEyeNote && (
                        <p className="text-sm opacity-90 border-t pt-2 mt-2">{matchResult.details.crossEyeNote}</p>
                      )}
                    </AlertDescription>
                  </Alert>

                  <div className="grid md:grid-cols-2 gap-4">
                    <Card className="bg-gray-50">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-lg">Match Score</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="relative pt-4">
                          <div className="flex mb-2 items-center justify-between">
                            <span className="text-xs font-semibold text-gray-600">0%</span>
                            <span className="text-xs font-semibold text-gray-600">100%</span>
                          </div>
                          <div className="overflow-hidden h-4 text-xs flex rounded-full bg-gray-200">
                            <div
                              style={{ width: `${scoreBarWidth()}%` }}
                              className={`shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center transition-all duration-500 ${
                                matchResult.status === 'MATCHED' ? 'bg-green-500' : 'bg-yellow-500'
                              }`}
                            />
                          </div>
                          <div
                            className="absolute top-4 w-0.5 h-6 bg-red-500"
                            style={{ left: `${thresholdMarkerLeft()}%` }}
                          />
                          <p className="text-center mt-3 text-2xl font-bold text-primary">{matchResult.confidence}%</p>
                        </div>
                      </CardContent>
                    </Card>

                    <Card className="bg-gray-50">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-lg">Match Details</CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Algorithm:</span>
                          <span className="font-medium text-xs text-right max-w-[55%]">{matchResult.details.algorithm}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Sample eye:</span>
                          <span className="font-medium">{matchResult.details.sampleIrisType}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Gallery eye:</span>
                          <span className="font-medium">{matchResult.details.galleryIrisType}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Rule:</span>
                          <span className="font-medium text-xs">{matchResult.details.matchCondition}</span>
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  <Card className="timing-card">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Clock className="h-4 w-4" />
                        Timing Details
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-4 gap-4 text-sm text-center">
                        <div>
                          <p className="text-gray-600">Sample → ISO</p>
                          <p className="font-medium">{timing.sampleConvert.toFixed(2)} ms</p>
                        </div>
                        <div>
                          <p className="text-gray-600">Gallery → ISO</p>
                          <p className="font-medium">{timing.galleryConvert.toFixed(2)} ms</p>
                        </div>
                        <div>
                          <p className="text-gray-600">Match</p>
                          <p className="font-medium text-primary">{timing.matchTime.toFixed(2)} ms</p>
                        </div>
                        <div>
                          <p className="text-gray-600">Total</p>
                          <p className="font-bold">{timing.total.toFixed(2)} ms</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default IrisMatcher;
